import { gql, useMutation, useQuery } from "@apollo/client";
import { getProcedureRoute, prepareTitleForProcedureRoute } from "@mx/gpl-common";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import Script from "next/script";
import React from "react";
import Error from "../../components/Error/Error";
import Loading from "../../components/Loading/Loading";
import ProcedureLayout from "../../components/ProcedureLayout/ProcedureLayout";
import SimilarProceduresGrid from "../../components/SimilarProceduresGrid/SimilarProceduresGrid";
import { getPublicRuntimeConfig } from "../../config";
import PageLayoutContainer from "../../containers/Layouts/PageLayoutContainer";
import ProcedureListingFragment from "../../containers/ProcedureListingFragment";
import { ensureError } from "../../core/error";
import getParam, { getArrayParam } from "../../core/getParam";
import { useDidMount } from "../../core/hooks/useDidUpdate";
import logger from "../../core/logger";
import { initializeApollo } from "../../graphql/client";
import {
  IDownloadProcedureMutation,
  IDownloadProcedureMutationVariables,
  IIncreaseViewCountMutationMutation,
  IIncreaseViewCountMutationMutationVariables,
  IProcedurePageQuery,
  IProcedurePageQueryVariables,
  ISimilarProceduresQuery,
  ISimilarProceduresQueryVariables,
} from "../../graphql/gpl-graphQLTypes.gen";

const procedureQuery = gql`
  query ProcedurePage($uid: String!) {
    procedure(uid: $uid) {
      ...ProcedureListingFragment
      source {
        name
        url
      }
      description
      scoringEnabled
      maxScore
      introduction
      rows {
        id
        parentId
        label
        type
        required
        description
        choices {
          label
          color
          score
        }
        maxScore
        condition
        attachment {
          id
          mimeType
          fileName
          originalUrl
          createdAt
          ... on ImageAttachment {
            resizableUrl
            width
            height
          }
        }
        attachments {
          id
          mimeType
          fileName
          originalUrl
          createdAt
          ... on ImageAttachment {
            resizableUrl
            width
            height
          }
        }
      }
    }
  }
  ${ProcedureListingFragment}
`;

const similarProceduresQuery = gql`
  query SimilarProcedures($uid: String!) {
    similarProcedures(uid: $uid, pagination: { limit: 50 }) {
      cursor
      data {
        ...ProcedureListingFragment
      }
      hasMore
    }
  }
  ${ProcedureListingFragment}
`;

export const increaseViewCountMutation = gql`
  mutation IncreaseViewCountMutation($input: IncreaseViewCountInput!) {
    increaseViewCount(input: $input) {
      success
      errors {
        fieldPath
        fieldValue
        error
      }
      newCount
    }
  }
`;

const downloadProcedureMutation = gql`
  mutation DownloadProcedure($input: DownloadProcedureInput!) {
    downloadProcedure(input: $input) {
      download {
        filename
        url
      }
      success
      errors {
        fieldPath
        fieldValue
        error
      }
    }
  }
`;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const apolloClient = initializeApollo();
  const [uid, ...titleRaw] = getArrayParam(context, "procedureslug") ?? "";

  if (!uid) {
    return { notFound: true };
  }

  const [, procedureQueryResult] = await Promise.all([
    PageLayoutContainer.runDataQueries(apolloClient),
    apolloClient.query<IProcedurePageQuery, IProcedurePageQueryVariables>({
      query: procedureQuery,
      variables: { uid },
    }),
    apolloClient.query<ISimilarProceduresQuery, ISimilarProceduresQueryVariables>({
      query: similarProceduresQuery,
      variables: { uid },
    }),
  ]);

  const procedure = procedureQueryResult.data.procedure;
  const titleFromParams = titleRaw.join(" ").trim();
  const titleFromProc = prepareTitleForProcedureRoute(
    procedureQueryResult.data.procedure?.title ?? "",
  );

  if (procedure && titleFromParams !== titleFromProc) {
    return {
      redirect: {
        destination: getProcedureRoute(procedure.uid, procedure.title),
        permanent: true,
      },
    };
  }

  return {
    props: { initialApolloState: apolloClient.cache.extract() },
  };
};

const ProcedureSlugPage = (props: { canonical: string }) => {
  const router = useRouter();
  const uid = getParam(router, "procedureslug") ?? "";

  const { data, loading, error } = useQuery<IProcedurePageQuery, IProcedurePageQueryVariables>(
    procedureQuery,
    { variables: { uid } },
  );

  const { data: similarProceduresData } = useQuery<
    ISimilarProceduresQuery,
    ISimilarProceduresQueryVariables
  >(similarProceduresQuery, { variables: { uid } });

  const [increaseViewCount] = useMutation<
    IIncreaseViewCountMutationMutation,
    IIncreaseViewCountMutationMutationVariables
  >(increaseViewCountMutation);

  const [downloadProcedure] = useMutation<
    IDownloadProcedureMutation,
    IDownloadProcedureMutationVariables
  >(downloadProcedureMutation);

  useDidMount(() => {
    const timeout = setTimeout(async () => {
      try {
        await increaseViewCount({ variables: { input: { procedureUid: uid } } });
      } catch (err) {
        logger.error(ensureError(err), { from: "increaseViewCount" });
      }
    }, 10000);
    return () => clearTimeout(timeout);
  });

  const handleDownload = React.useCallback(
    async (email: string) => {
      const downloadResp = await downloadProcedure({
        variables: { input: { email, procedureUid: uid } },
      });
      return (
        downloadResp.data?.downloadProcedure ?? {
          __typename: "DownloadProcedureResponse" as const,
          success: false,
          download: undefined,
          errors: undefined,
        }
      );
    },
    [uid, downloadProcedure],
  );

  function renderContent() {
    if (data?.procedure) {
      return <ProcedureLayout procedure={data.procedure} onDownload={handleDownload} />;
    }

    if (loading) {
      return <Loading />;
    }

    if (error) {
      return <Error error={error} />;
    }

    return null;
  }

  return (
    <PageLayoutContainer
      additionalContent={
        similarProceduresData?.similarProcedures.data.length ? (
          <SimilarProceduresGrid
            data={similarProceduresData.similarProcedures.data}
            // Make sure we don't render the same procedure in the similar
            except={data?.procedure?.uid}
          />
        ) : null
      }
      hideFilter={true}
      hideSidebar={true}
      metaDescription={data?.procedure?.description ?? undefined}
      metaTitle={data?.procedure?.title ?? undefined}
      metaUrl={
        data?.procedure
          ? `${getPublicRuntimeConfig().frontendUrl}${getProcedureRoute(
              data.procedure.uid,
              data.procedure.title,
            )}`
          : undefined
      }
    >
      {!!data?.procedure && (
        <Script
          id="procedure-script"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "http://schema.org/",
              "@type": "DigitalDocument",
              name: data.procedure.title,
              description: data.procedure.description,
              author: data.procedure.maintainer?.name ?? "MaintainX",
              isBasedOn: data.procedure.source
                ? {
                    "@type": "DigitalDocument",
                    "schema:name": "Original procedure",
                    "schema:publisher": data.procedure.source.name,
                    "schema:url": data.procedure.source.url,
                  }
                : undefined,
            }),
          }}
        />
      )}
      {renderContent()}
    </PageLayoutContainer>
  );
};

export default ProcedureSlugPage;
